import { lookDirection, lookPosition, type Scan, type ScanGeometry } from './scan.ts';
import type { Vec3 } from './geom3.ts';

/**
 * Backprojection: many one-dimensional profiles into one three-dimensional map.
 *
 * The idea is the oldest one in imaging. A single measurement says "something
 * reflects at 2.2 metres" and draws a sphere, because every point on that
 * sphere fits equally well. Take a second measurement from 80 cm to the left
 * and it draws a different sphere. The two intersect in a circle; a third
 * cuts that to a pair of points; a scan's worth cuts it to the surface that
 * was actually there. Nothing is inferred that was not measured - the map is
 * just the set of places every profile agrees a reflector could be.
 *
 * Two things supply that parallax, and they are not equal:
 *
 *   Directivity. A phone is a baffle several wavelengths across at the top of
 *   the sweep, so the two-way response really is peaked forward - but broadly,
 *   tens of degrees. On its own it blurs a room into a smear.
 *
 *   Translation. Turning with the phone held out at arm's length swings it
 *   round a circle over a metre across, and the range to a fixed surface then
 *   rises and falls across the scan by up to that much - against a range
 *   resolution of about a centimetre. This is the term that actually resolves
 *   the room, and it is why the app asks for an outstretched arm rather than
 *   a phone held against the chest.
 *
 * This grid answers a different question from the plane search, and the two
 * are deliberately not merged. A wall reflects specularly - the echo comes
 * from whichever patch is square-on at that instant, and the patch slides
 * along the wall as the phone moves - so treating it as a point scatterer
 * paints spheres tangent to it and finds nothing. A chair is the opposite
 * case: small, curved, edges diffracting, scattering weakly back towards
 * wherever the sound came from. That is exactly what backprojection is for,
 * and it is why the room's surfaces are fitted as planes while the things
 * standing in the room are found here.
 */

export interface VoxelGrid {
  /** World position of the centre of voxel (0,0,0). */
  originM: Vec3;
  stepM: number;
  nx: number;
  ny: number;
  nz: number;
  /**
   * How far this cell stands above the reverberation arriving at the same
   * instant, in dB, combined across every look that could see it.
   */
  valuesDb: Float32Array;
  /**
   * Angular diversity, 0..1. How much the scan moved *around* this cell as
   * opposed to merely towards it. Near zero, the cell's range is well known
   * and its position is not, and the renderer fades it out accordingly.
   */
  support: Float32Array;
  /** Total beam weight that reached the cell. Cells nobody looked at read 0. */
  coverage: Float32Array;
}

export interface BeamModel {
  /** Half-power half-angle of the two-way pattern, in degrees. */
  halfAngleDeg: number;
  /** Response outside the main lobe, 0..1. Never zero: phones are not horns. */
  backFloor: number;
}

export const DEFAULT_BEAM: BeamModel = { halfAngleDeg: 55, backFloor: 0.03 };

export interface BackprojectOptions {
  grid?: { stepM?: number; extentM?: number; floorM?: number; ceilingM?: number };
  beam?: BeamModel;
  /** Looks whose beam weight falls below this contribute nothing to a cell. */
  minBeamWeight?: number;
  /** Cells seen by fewer looks than this are left unreconstructed. */
  minLooks?: number;
  /** Ceiling on one look's contribution, so a single loud return cannot invent a cell. */
  maxExcessDb?: number;
  /** Cells further than this from the phone are not reconstructed. */
  maxRangeM?: number;
  /** Called with 0..1 so a long reconstruction can yield to paint. */
  onProgress?: (fraction: number) => void;
}

function beamExponent(beam: BeamModel): number {
  const half = Math.cos((beam.halfAngleDeg * Math.PI) / 180);
  const shape = 0.5 + 0.5 * half;
  if (shape <= 0 || shape >= 1) return 1;
  return Math.log(0.5) / Math.log(shape);
}

export function beamWeight(cosTheta: number, beam: BeamModel, exponent: number): number {
  const shape = Math.pow(Math.max(0, 0.5 + 0.5 * cosTheta), exponent);
  return beam.backFloor + (1 - beam.backFloor) * shape;
}

export function allocateGrid(
  stepM: number,
  extentM: number,
  floorM: number,
  ceilingM: number,
): VoxelGrid {
  const nx = Math.max(1, Math.ceil((2 * extentM) / stepM) + 1);
  const nz = nx;
  const ny = Math.max(1, Math.ceil((ceilingM - floorM) / stepM) + 1);
  const count = nx * ny * nz;
  return {
    originM: [-extentM, floorM, -extentM],
    stepM,
    nx,
    ny,
    nz,
    valuesDb: new Float32Array(count),
    support: new Float32Array(count),
    coverage: new Float32Array(count),
  };
}

export function gridIndex(grid: VoxelGrid, ix: number, iy: number, iz: number): number {
  return (iy * grid.nz + iz) * grid.nx + ix;
}

export function voxelCentre(grid: VoxelGrid, ix: number, iy: number, iz: number): Vec3 {
  return [
    grid.originM[0] + ix * grid.stepM,
    grid.originM[1] + iy * grid.stepM,
    grid.originM[2] + iz * grid.stepM,
  ];
}

/**
 * Reconstruct the occupancy grid for a scan.
 *
 * Voxel-outer and look-inner on purpose. The accumulators then live in
 * registers for the whole inner loop and the profiles - a few hundred floats
 * each - stay in cache, which is the difference between a reconstruction that
 * runs while the user watches and one that does not.
 */
export function backproject(scan: Scan, options: BackprojectOptions = {}): VoxelGrid {
  const beam = options.beam ?? DEFAULT_BEAM;
  const exponent = beamExponent(beam);
  const minWeight = options.minBeamWeight ?? 0.3;
  const minLooks = options.minLooks ?? 4;
  const maxPowerRatio = Math.pow(10, (options.maxExcessDb ?? 20) / 10);
  const axis = scan.rangeAxis;
  const maxRangeM = options.maxRangeM ?? axis.originM + (axis.count - 1) * axis.stepM;

  const stepM = options.grid?.stepM ?? 0.06;
  const extentM = options.grid?.extentM ?? Math.min(maxRangeM, 8);
  const floorM = options.grid?.floorM ?? -2.2;
  const ceilingM = options.grid?.ceilingM ?? 1.6;
  const grid = allocateGrid(stepM, extentM, floorM, ceilingM);

  const looks = scan.looks;
  const n = looks.length;
  if (n === 0) return grid;

  // Flatten the per-look constants once. Property access on N objects inside a
  // loop that runs a hundred million times is not free.
  const px = new Float64Array(n);
  const py = new Float64Array(n);
  const pz = new Float64Array(n);
  const bx = new Float64Array(n);
  const by = new Float64Array(n);
  const bz = new Float64Array(n);
  const envelopes: Float32Array[] = [];
  const backgrounds: Float32Array[] = [];
  for (let k = 0; k < n; k++) {
    const p = lookPosition(looks[k], scan.geometry);
    const d = lookDirection(looks[k], scan.geometry);
    px[k] = p[0];
    py[k] = p[1];
    pz[k] = p[2];
    bx[k] = d[0];
    by[k] = d[1];
    bz[k] = d[2];
    envelopes.push(looks[k].envelope);
    backgrounds.push(looks[k].background);
  }

  const blind = scan.blindRangeM;
  const invStep = 1 / axis.stepM;
  const origin = axis.originM;
  const lastBin = axis.count - 1;

  for (let iy = 0; iy < grid.ny; iy++) {
    const wy = grid.originM[1] + iy * grid.stepM;
    for (let iz = 0; iz < grid.nz; iz++) {
      const wz = grid.originM[2] + iz * grid.stepM;
      for (let ix = 0; ix < grid.nx; ix++) {
        const wx = grid.originM[0] + ix * grid.stepM;

        let weightSum = 0;
        let valueSum = 0;
        let dirX = 0;
        let dirY = 0;
        let dirZ = 0;
        let contributors = 0;

        for (let k = 0; k < n; k++) {
          const dx = wx - px[k];
          const dy = wy - py[k];
          const dz = wz - pz[k];
          const r = Math.sqrt(dx * dx + dy * dy + dz * dz);
          if (r < blind || r > maxRangeM) continue;

          const inv = 1 / r;
          const cosTheta = (dx * bx[k] + dy * by[k] + dz * bz[k]) * inv;
          const w = beamWeight(cosTheta, beam, exponent);
          // Looks that could not see this cell are dropped, not averaged in as
          // agreement that nothing is there. Including them looks more
          // conservative and is simply wrong: a scatterer is invisible behind
          // the handset, so a rearward look carries no evidence either way,
          // and letting fifty of them vote "nothing" drags every real object
          // back down to the noise. That dilution compressed the entire volume
          // into a six-decibel band with nothing left to threshold.
          if (w < minWeight) continue;

          const t = (r - origin) * invStep;
          if (t < 0 || t > lastBin) continue;
          const i0 = t | 0;
          const i1 = i0 < lastBin ? i0 + 1 : lastBin;
          const frac = t - i0;
          const env = envelopes[k];
          const bg = backgrounds[k];
          const amplitude = env[i0] * (1 - frac) + env[i1] * frac;
          const background = bg[i0] * (1 - frac) + bg[i1] * frac;

          // Power against the local reverberation: CFAR as a continuous field.
          // Power, not decibels, because incoherent integration only pays its
          // square root when what is averaged is proportional to energy.
          const ratio = amplitude / Math.max(background, 1e-12);
          const powerRatio = Math.min(maxPowerRatio, ratio * ratio);

          weightSum += w;
          valueSum += w * powerRatio;
          contributors++;

          dirX += w * dx * inv;
          dirY += w * dy * inv;
          dirZ += w * dz * inv;
        }

        const index = gridIndex(grid, ix, iy, iz);
        if (weightSum <= 0 || contributors < minLooks) continue;

        const mean = valueSum / weightSum;
        grid.valuesDb[index] = 10 * Math.log10(Math.max(mean, 1e-6));
        grid.coverage[index] = weightSum;

        // A short resultant means the scan looked at this cell from genuinely
        // different bearings; a long one means every look saw it from the same
        // side, and its range is far better known than its position.
        const resultant = Math.sqrt(dirX * dirX + dirY * dirY + dirZ * dirZ) / weightSum;
        grid.support[index] = 1 - Math.min(1, resultant);
      }
    }
    options.onProgress?.((iy + 1) / grid.ny);
  }

  return grid;
}

/**
 * A focus score for a reconstruction, used to choose the geometry rather than
 * ask the user for it.
 *
 * Sharpness, in the sense image reconstruction has always meant it: a map
 * built with the right aperture radius and the right boresight concentrates
 * its energy into surfaces, and one built with the wrong parameters spreads
 * the same energy over arcs. Normalised kurtosis rewards exactly that
 * concentration and is blind to overall brightness, so it can compare two
 * reconstructions that differ in scale.
 */
export function focusScore(grid: VoxelGrid): number {
  const v = grid.valuesDb;
  let count = 0;
  let sum = 0;
  for (let i = 0; i < v.length; i++) {
    if (grid.coverage[i] <= 0) continue;
    count++;
    sum += v[i];
  }
  if (count < 8) return 0;
  const mean = sum / count;

  let m2 = 0;
  let m4 = 0;
  for (let i = 0; i < v.length; i++) {
    if (grid.coverage[i] <= 0) continue;
    const d = v[i] - mean;
    const d2 = d * d;
    m2 += d2;
    m4 += d2 * d2;
  }
  m2 /= count;
  m4 /= count;
  return m2 > 1e-12 ? m4 / (m2 * m2) : 0;
}

export interface AutofocusResult {
  geometry: ScanGeometry;
  score: number;
  /** Every candidate tried, best first - shown in the data drawer. */
  trials: { armRadiusM: number; boresightId: string; score: number }[];
}

/**
 * Choose the aperture radius and the acoustic boresight by trying them.
 *
 * Neither is knowable in advance. The web platform will not say which way a
 * handset radiates, and nobody can report their own arm length to the
 * centimetre while turning on the spot. Both, however, are strongly
 * observable *after* the fact: get either wrong and every surface in the map
 * smears along an arc. So the reconstruction is run at reduced resolution
 * across a small grid of candidates and the sharpest one wins - the same
 * autofocus a synthetic-aperture radar runs, for the same reason.
 */
export function autofocus(
  scan: Scan,
  candidates: { armRadiiM: number[]; boresights: { id: string; axis: Vec3 }[] },
  options: BackprojectOptions = {},
): AutofocusResult {
  const trials: { armRadiusM: number; boresightId: string; score: number }[] = [];
  let best: AutofocusResult | null = null;

  for (const boresight of candidates.boresights) {
    for (const armRadiusM of candidates.armRadiiM) {
      const geometry: ScanGeometry = { armRadiusM, boresight: boresight.axis };
      const trial = backproject(
        { ...scan, geometry },
        { ...options, grid: { ...options.grid, stepM: options.grid?.stepM ?? 0.14 } },
      );
      const score = focusScore(trial);
      trials.push({ armRadiusM, boresightId: boresight.id, score });
      if (!best || score > best.score) best = { geometry, score, trials };
    }
  }

  trials.sort((a, b) => b.score - a.score);
  return best ?? { geometry: scan.geometry, score: 0, trials };
}
